import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Info, DollarSign, Briefcase, Code, FileText, Check, X, Plus, GraduationCap, BrainCircuit, Calendar, Building2, ThumbsUp, Asterisk, Lightbulb, List } from "lucide-react";
import Select from "react-select";
import strings from '../../../utils/Strings';
import JobOpportunityClient, { JobOpportunity } from "../data/api/JobOpportunityClient";
import OrganizationClient, { OrganizationProfile } from "../../organizations/data/api/OrganizationClient";
import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";
import DateTimePicker from "../../../shared/components/DateTimePicker";
import { RichTextEditor } from "../../../shared/components/RichTextEditor/RichTextEditor";
import ResumeViewer, { ResumeZoomControls, ResumeViewerHandle } from "../../../shared/components/ResumeViewer";
import ReactDOM from "react-dom";
import "../JobOpportunity.css";

const HiringTypes = ["Internal", "Organization"];
const JobTypes = ["Full time", "Part time", "Contractor"];
const PaymentTypes = ["Annual", "Hourly"];
const WorkTypes = ["Remote", "Hybrid", "On-site"];
const Statuses = ["Draft", "Open", "Closed"];

const toDateInput = (value?: string) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    return value;
  }
  return d.toISOString().slice(0, 10);
};

const EDUCATION_OPTIONS = strings.learner.degreesList;

const customSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    border: state.isFocused ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    color: 'var(--text-heading)',
    background: 'var(--surface)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--primary-ghost)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--primary)' : 'var(--border)'
    },
    outline: 'none',
    transition: 'border-color var(--transition), box-shadow var(--transition)',
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 12px' }),
  singleValue: (base: any, state: any) => ({ ...base, color: state.isDisabled ? 'var(--text-muted)' : 'var(--text-heading)' }),
  input: (base: any) => ({
    ...base,
    color: 'var(--text-heading)',
    margin: '0',
    padding: '0',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    minHeight: 'auto',
    height: 'auto',
    'input:focus': {
      boxShadow: 'none',
      border: 'none',
      outline: 'none'
    }
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({ ...base, padding: '4px' }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, { isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isSelected ? 'var(--primary)' : isFocused ? 'var(--primary-light)' : 'var(--surface)',
    color: isSelected ? '#fff' : 'var(--text-heading)',
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--primary)',
      color: '#fff'
    }
  })
};

const JobOpportunityFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [docPreviewUrl, setDocPreviewUrl] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docZoom, setDocZoom] = useState(1);
  const docViewerRef = useRef<ResumeViewerHandle>(null);

  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [forOrganizationName, setForOrganizationName] = useState("");
  const [forOrganizationId, setForOrganizationId] = useState("");
  const [applicationDeadline, setApplicationDeadline] = useState("");
  const [workLocation, setWorkLocation] = useState("");
  const [hiringType, setHiringType] = useState<string>("Internal");
  const [jobType, setJobType] = useState<string>("Full time");
  const [minYearsExperience, setMinYearsExperience] = useState<number | "">("");
  const [paymentType, setPaymentType] = useState<string>("Annual");
  const [minCtc, setMinCtc] = useState<number | "">("");
  const [maxCtc, setMaxCtc] = useState<number | "">("");
  const [minHourlyRate, setMinHourlyRate] = useState<number | "">("");
  const [maxHourlyRate, setMaxHourlyRate] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [skillsReq, setSkillsReq] = useState<string[]>([]);
  const [skillsPref, setSkillsPref] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState("");
  const [skillType, setSkillType] = useState<"Req" | "Pref">("Req");
  const [workType, setWorkType] = useState<string>("On-site");
  const [status, setStatus] = useState<string>("Draft");
  const [stages, setStages] = useState<string[]>([
    "Applied", "Shortlisted", "Interviewed", "Offered", "Accepted", "Declined", "Rejected"
  ]);
  const [newStage, setNewStage] = useState("");
  const [draggedStageIdx, setDraggedStageIdx] = useState<number | null>(null);
  const [currency, setCurrency] = useState("USD ($)");
  const [sourceDocUrl, setSourceDocUrl] = useState<string | null>(null);
  const [sourceDocName, setSourceDocName] = useState<string | null>(null);

  const [maxYearsExperience, setMaxYearsExperience] = useState<number | "">("");
  const [vacancies, setVacancies] = useState<number | "">("");
  const [educationLevel, setEducationLevel] = useState("Any");
  const [isEducationDropdown, setIsEducationDropdown] = useState(true);

  const [activeSection, setActiveSection] = useState("context");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);

  const [organizations, setOrganizations] = useState<OrganizationProfile[]>([]);

  useEffect(() => {
    OrganizationClient.listOrganizations(1, 1000).then(data => {
      setOrganizations(data.records || []);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      setLoading(true);
      JobOpportunityClient.getOpportunity(id)
        .then((o) => {
          setTitle(o.title || "");
          setDepartment(o.department || "");
          setForOrganizationName(o.for_organization_name || "");
          setForOrganizationId(o.for_organization_id || "");
          setApplicationDeadline(toDateInput(o.application_deadline));
          setVacancies(o.vacancies ?? "");
          setWorkLocation(o.work_location || "");
          setHiringType(o.hiring_type || "Internal");
          setJobType(o.job_type || "Full time");
          setMinYearsExperience(o.min_years_experience ?? "");
          setMaxYearsExperience(o.max_years_experience ?? "");
          setPaymentType(o.payment_type || "Annual");
          setMinCtc(o.min_ctc ?? "");
          setMaxCtc(o.max_ctc ?? "");
          setMinHourlyRate(o.min_hourly_rate ?? "");
          setMaxHourlyRate(o.max_hourly_rate ?? "");
          setDescription(o.description || "");
          setSkillsReq(o.skills_required || []);
          setSkillsPref(o.skills_preferred || []);

          if (o.education_level) {
            setEducationLevel(o.education_level);
            if (!EDUCATION_OPTIONS.includes(o.education_level)) {
              setIsEducationDropdown(false);
            }
          }

          if (o.stages && o.stages.length > 0) {
            setStages(o.stages);
          }
          setWorkType(o.work_type || "On-site");
          setStatus(o.status || "Draft");
          
          if (o.source_document_url) {
            setSourceDocUrl(o.source_document_url);
            setSourceDocName(o.source_document_name || "View Uploaded Document");
          }
        })
        .catch((err: any) => {
          let msg = err.message || "Failed to load job opportunity";
          if (err?.response?.data?.detail) {
            const detail = err.response.data.detail;
            msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d: any) => d.msg).join(', ') : JSON.stringify(detail);
          }
          setError(msg);
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const visibleEntries = entries.filter(entry => entry.isIntersecting);
      if (visibleEntries.length > 0) {
        visibleEntries.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        setActiveSection(visibleEntries[0].target.id);
      }
    }, {
      root: null,
      rootMargin: "-100px 0px -40% 0px",
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });

    const sections = ["context", "basic", "compensation", "requirements", "stages", "description"];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleAddSkill = (e: React.KeyboardEvent | React.MouseEvent) => {
    if ((e.type === "keydown" && (e as React.KeyboardEvent).key !== "Enter") || !skillInput.trim()) return;
    e.preventDefault();
    const newSkill = skillInput.trim();
    if (!skillsReq.includes(newSkill) && !skillsPref.includes(newSkill)) {
      if (skillType === "Req") {
        setSkillsReq([...skillsReq, newSkill]);
      } else {
        setSkillsPref([...skillsPref, newSkill]);
      }
    }
    setSkillInput("");
  };

  const handleRemoveSkillReq = (skill: string) => {
    setSkillsReq(skillsReq.filter((s) => s !== skill));
  };
  const handleRemoveSkillPref = (skill: string) => {
    setSkillsPref(skillsPref.filter((s) => s !== skill));
  };

  const validate = (): string | null => {
    if (!title.trim()) return "Title is required";
    if (!applicationDeadline.trim()) return "Application deadline is required";
    if (!workLocation.trim()) return "Work location is required";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setShowConfirmAlert(true);
  };

  const confirmSubmit = async () => {
    setShowConfirmAlert(false);

    const payload: Partial<JobOpportunity> = {
      title: title.trim(),
      department: department.trim() || null,
      for_organization_name: forOrganizationName.trim() || null,
      for_organization_id: forOrganizationId.trim() || null,
      application_deadline: applicationDeadline,
      vacancies: vacancies !== "" ? Number(vacancies) : null,
      work_location: workLocation.trim(),
      hiring_type: hiringType,
      job_type: jobType,
      min_years_experience: Number(minYearsExperience) || 0,
      max_years_experience: maxYearsExperience !== "" ? Number(maxYearsExperience) : null,
      education_level: educationLevel.trim() || null,
      payment_type: paymentType,
      min_ctc: paymentType === "Annual" ? (Number(minCtc) || null) : null,
      max_ctc: paymentType === "Annual" ? (Number(maxCtc) || null) : null,
      min_hourly_rate: paymentType === "Hourly" ? (Number(minHourlyRate) || null) : null,
      max_hourly_rate: paymentType === "Hourly" ? (Number(maxHourlyRate) || null) : null,
      description: description.trim() || null,
      skills_required: skillsReq,
      skills_preferred: skillsPref,
      stages: stages,
      work_type: workType,
      status,
    };

    setSaving(true);
    setError(null);
    try {
      if (isEditing && id) {
        await JobOpportunityClient.updateOpportunity(id, payload);
      } else {
        await JobOpportunityClient.createOpportunity(payload as JobOpportunity);
      }
      navigate("/dashboard/job-opportunities");
    } catch (err: any) {
      let msg = err.message || "Failed to save job opportunity";
      if (err?.response?.data?.detail) {
        const detail = err.response.data.detail;
        msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d: any) => `${d.loc?.join('.')} - ${d.msg}`).join(', ') : JSON.stringify(detail);
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="learner-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader isLoading={loading || saving} message={saving ? "Saving job opportunity..." : "Loading job opportunity..."} />

      <Alert
        show={showConfirmAlert}
        type="info"
        title={isEditing ? "Update Job Opportunity" : "Publish Job Opportunity"}
        message={`Are you sure you want to ${isEditing ? "update" : "publish"} the job opportunity for <b>${title.trim()}</b>?`}
        primaryButtonText={isEditing ? "Update" : "Publish"}
        onPrimaryClick={confirmSubmit}
        onClose={() => setShowConfirmAlert(false)}
      />

      <div className="jo-page-nav" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button className="jo-nav-back-btn" onClick={() => navigate("/dashboard/job-opportunities")}>
            <ArrowLeft size={18} />
          </button>
          <h1 className="jo-nav-title">{isEditing ? "Edit Job Opportunity" : "Create Job Opportunity"}</h1>
        </div>
        {sourceDocUrl && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setDocPreviewUrl(sourceDocUrl); setDocLoading(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', 
              padding: '8px 16px', background: 'var(--primary-light)', 
              color: 'var(--primary)', borderRadius: '24px', 
              fontSize: '0.9rem', fontWeight: 500, textDecoration: 'none',
              border: '1px solid var(--primary)', cursor: 'pointer'
            }}
          >
            <FileText size={16} /> {sourceDocName}
          </button>
        )}
      </div>


      {error && <div className="jo-error-banner">{error}</div>}

      {!loading && (
        <form onSubmit={handleSubmit} className="jo-form-layout">
          {/* Sidebar */}
          {/* Sidebar */}
          <div className="jo-sidebar-stepper-container">
            <div className="jo-stepper">
              {[
                { id: "context", label: "Hiring Context", icon: <Building2 size={16} /> },
                { id: "basic", label: "Basic Details", icon: <Info size={16} /> },
                { id: "compensation", label: "Compensation", icon: <DollarSign size={16} /> },
                { id: "requirements", label: "Requirements", icon: <Briefcase size={16} /> },
                { id: "stages", label: "Pipeline Stages", icon: <List size={16} /> },
                { id: "description", label: "Description", icon: <FileText size={16} /> }
              ].map((step, index, arr) => {
                const normalizedActive = activeSection === "skills" ? "requirements" : activeSection;
                const activeIndex = arr.findIndex(s => s.id === normalizedActive);
                const isCompleted = index < activeIndex;
                const isActive = index === activeIndex;
                
                return (
                  <div 
                    key={step.id} 
                    className={`jo-step-item ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                    onClick={() => scrollToSection(step.id)}
                  >
                    <div className="jo-step-icon-wrapper">
                      <div className="jo-step-icon">
                        {isCompleted ? <Check size={16} strokeWidth={3} /> : step.icon}
                      </div>
                      {index < arr.length - 1 && <div className="jo-step-line" />}
                    </div>
                    <div className="jo-step-content">
                      <div className="jo-step-subtitle">STEP {index + 1}</div>
                      <div className="jo-step-title">{step.label}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="jo-content">

            {/* Hiring Context */}
            <div className="jo-section-card" id="context">
              <div className="jo-section-header">
                <div className="jo-section-icon"><Building2 size={18} /></div>
                <h3 className="jo-section-title">Hiring Context</h3>
              </div>

              <div className="jo-inline-group">
                <div className="jo-form-group" style={{ flex: 1 }}>
                  <label>Hiring Type</label>
                  <div style={{ marginBottom: 0 }}>
                    <div style={{ display: 'inline-flex', background: 'rgba(13, 110, 91, 0.04)', borderRadius: '30px', padding: '4px', alignItems: 'center', border: '1px solid rgba(13, 110, 91, 0.08)' }}>
                      <button
                        type="button"
                        onClick={() => setHiringType("Internal")}
                        style={{
                          padding: '8px 24px', borderRadius: '24px',
                          border: hiringType === "Internal" ? '1px solid rgba(13, 110, 91, 0.1)' : '1px solid transparent',
                          background: hiringType === "Internal" ? 'rgba(13, 110, 91, 0.85)' : 'transparent',
                          backdropFilter: hiringType === "Internal" ? 'blur(8px)' : 'none',
                          color: hiringType === "Internal" ? '#ffffff' : '#475569',
                          fontWeight: hiringType === "Internal" ? 500 : 400,
                          fontSize: '0.9rem', fontFamily: 'inherit', cursor: 'pointer',
                          boxShadow: hiringType === "Internal" ? '0 4px 10px rgba(13, 110, 91, 0.15)' : 'none',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >Internal</button>
                      <button
                        type="button"
                        onClick={() => setHiringType("Organization")}
                        style={{
                          padding: '8px 24px', borderRadius: '24px',
                          border: hiringType === "Organization" ? '1px solid rgba(13, 110, 91, 0.1)' : '1px solid transparent',
                          background: hiringType === "Organization" ? 'rgba(13, 110, 91, 0.85)' : 'transparent',
                          backdropFilter: hiringType === "Organization" ? 'blur(8px)' : 'none',
                          color: hiringType === "Organization" ? '#ffffff' : '#475569',
                          fontWeight: hiringType === "Organization" ? 500 : 400,
                          fontSize: '0.9rem', fontFamily: 'inherit', cursor: 'pointer',
                          boxShadow: hiringType === "Organization" ? '0 4px 10px rgba(13, 110, 91, 0.15)' : 'none',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >Organization</button>
                    </div>
                  </div>
                </div>

                {hiringType === "Organization" && (
                  <div className="jo-form-group" style={{ flex: 1 }}>
                    <label>Select Organization</label>
                    <Select
                      options={organizations.map((org) => ({ value: org.organization_id || "", label: org.name }))}
                      value={forOrganizationId ? { value: forOrganizationId, label: forOrganizationName } : null}
                      onChange={(val: any) => {
                        setForOrganizationName(val?.label || "");
                        setForOrganizationId(val?.value || "");
                      }}
                      styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                      placeholder="Search organization..."
                      classNamePrefix="rs"
                      isClearable
                    />
                  </div>
                )}

                {hiringType !== "Organization" && (
                  <div className="jo-form-group" style={{ flex: 1, visibility: 'hidden' }}></div>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="jo-section-card" id="basic">
              <div className="jo-section-header">
                <div className="jo-section-icon"><Info size={18} /></div>
                <h3 className="jo-section-title">Basic Information</h3>
              </div>

              <div className="jo-form-group">
                <label>Job Title <span className="jo-required">*</span></label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Senior Frontend Engineer" />
              </div>

              <div className="jo-inline-group">
                <div className="jo-form-group">
                  <label>Department</label>
                  <input type="text" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Engineering" />
                </div>
                <div className="jo-form-group">
                  <label>Location <span className="jo-required">*</span></label>
                  <input type="text" value={workLocation} onChange={(e) => setWorkLocation(e.target.value)} placeholder="e.g. New York, NY" />
                </div>
              </div>

              <div className="jo-inline-group">
                <div className="jo-form-group">
                  <label>Job Type</label>
                  <Select
                    options={JobTypes.map((jt) => ({ value: jt, label: jt }))}
                    value={{ value: jobType, label: jobType }}
                    onChange={(val: any) => setJobType(val?.value || "Full time")}
                    styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                    classNamePrefix="rs"
                  />
                </div>
                <div className="jo-form-group">
                  <label>Status</label>
                  <Select
                    options={Statuses.map((s) => ({ value: s, label: s }))}
                    value={{ value: status, label: status }}
                    onChange={(val: any) => setStatus(val?.value || "Draft")}
                    styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                    classNamePrefix="rs"
                  />
                </div>
              </div>

              <div className="jo-toggle" style={{ width: "100%", marginBottom: "20px" }}>
                <div className="jo-toggle-info">
                  <div className="jo-toggle-label">Remote Friendly</div>
                  <div className="jo-toggle-desc">Allow candidates to work from anywhere.</div>
                </div>
                <label className="jo-switch">
                  <input type="checkbox" checked={workType === "Remote" || workType === "Hybrid"} onChange={(e) => setWorkType(e.target.checked ? "Remote" : "On-site")} />
                  <span className="jo-slider"></span>
                </label>
              </div>

              <div className="jo-inline-group" style={{ marginBottom: 0 }}>
                <div className="jo-form-group" style={{ marginBottom: 0 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><Calendar size={16} /> Application Deadline <span className="jo-required">*</span></label>
                  <DateTimePicker
                    dateOnly={true}
                    value={applicationDeadline}
                    onChange={(val) => setApplicationDeadline(val)}
                    placeholder="dd-mm-yyyy"
                  />
                </div>
                <div className="jo-form-group" style={{ marginBottom: 0 }}>
                  <label>Number of Vacancies</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 5"
                    value={vacancies}
                    onChange={(e) => setVacancies(e.target.value === "" ? "" : Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* Compensation */}
            <div className="jo-section-card" id="compensation">
              <div className="jo-section-header">
                <div className="jo-section-icon"><DollarSign size={18} /></div>
                <h3 className="jo-section-title">Compensation</h3>
              </div>

              <div className="jo-inline-group">
                <div className="jo-form-group" style={{ marginBottom: 0 }}>
                  <label>Payment Type</label>
                  <Select
                    options={[
                      { value: "Annual", label: "Annual Salary" },
                      { value: "Hourly", label: "Hourly Rate" }
                    ]}
                    value={{ value: paymentType, label: paymentType === "Annual" ? "Annual Salary" : "Hourly Rate" }}
                    onChange={(val: any) => setPaymentType(val?.value || "Annual")}
                    styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                  />
                </div>
                <div className="jo-form-group" style={{ marginBottom: 0 }}>
                  <label>Currency</label>
                  <Select
                    options={[
                      { value: "USD ($)", label: "USD ($)" },
                      { value: "EUR (€)", label: "EUR (€)" },
                      { value: "GBP (£)", label: "GBP (£)" },
                      { value: "INR (₹)", label: "INR (₹)" }
                    ]}
                    value={{ value: currency, label: currency }}
                    onChange={(val: any) => setCurrency(val?.value || "USD ($)")}
                    styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                  />
                </div>
                <div className="jo-form-group" style={{ marginBottom: 0 }}>
                  <label>Range / Amount</label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                      type="number"
                      min={0}
                      value={paymentType === "Annual" ? minCtc : minHourlyRate}
                      onChange={(e) => paymentType === "Annual" ? setMinCtc(Number(e.target.value) || "") : setMinHourlyRate(Number(e.target.value) || "")}
                      placeholder="Min"
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: "var(--text-muted)", fontWeight: "500" }}>-</span>
                    <input
                      type="number"
                      min={0}
                      value={paymentType === "Annual" ? maxCtc : maxHourlyRate}
                      onChange={(e) => paymentType === "Annual" ? setMaxCtc(Number(e.target.value) || "") : setMaxHourlyRate(Number(e.target.value) || "")}
                      placeholder="Max"
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="jo-grid-2-col">

              <div className="jo-section-card" id="requirements">
                <div className="jo-section-header">
                  <div className="jo-section-icon"><GraduationCap size={18} /></div>
                  <h3 className="jo-section-title">Requirements</h3>
                </div>
                <div className="jo-form-group">
                  <label>Experience (Years)</label>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <Select
                        options={[{ value: "", label: "Min" }, ...[...Array(21)].map((_, i) => ({ value: i, label: `${i}` }))]}
                        value={{ value: minYearsExperience, label: minYearsExperience === "" ? "Min" : `${minYearsExperience}` }}
                        onChange={(val: any) => setMinYearsExperience(val?.value === "" ? "" : Number(val?.value))}
                        styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Select
                        options={[{ value: "", label: "Max (Optional)" }, ...[...Array(21)].map((_, i) => ({ value: i, label: `${i}` }))]}
                        value={{ value: maxYearsExperience, label: maxYearsExperience === "" ? "Max (Optional)" : `${maxYearsExperience}` }}
                        onChange={(val: any) => setMaxYearsExperience(val?.value === "" ? "" : Number(val?.value))}
                        styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                      />
                    </div>
                  </div>
                </div>
                <div className="jo-form-group" style={{ marginBottom: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <label style={{ margin: 0 }}>Education Level</label>
                    <button type="button" onClick={() => {
                      setIsEducationDropdown(!isEducationDropdown);
                      if (!isEducationDropdown && !EDUCATION_OPTIONS.includes(educationLevel)) {
                        setEducationLevel("");
                      }
                    }} style={{ background: "none", border: "none", color: "var(--primary)", fontSize: "0.75rem", cursor: "pointer", padding: 0, fontFamily: 'inherit' }}>
                      {isEducationDropdown ? 'Enter Manually' : 'Select from List'}
                    </button>
                  </div>
                  {isEducationDropdown ? (
                    <Select
                      options={EDUCATION_OPTIONS.map(opt => ({ value: opt, label: opt }))}
                      value={EDUCATION_OPTIONS.includes(educationLevel) ? { value: educationLevel, label: educationLevel } : null}
                      onChange={(val: any) => setEducationLevel(val?.value || '')}
                      placeholder="Select education level..."
                      styles={customSelectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                      classNamePrefix="rs"
                    />
                  ) : (
                    <input
                      type="text"
                      placeholder="Enter education level manually..."
                      value={educationLevel}
                      onChange={e => setEducationLevel(e.target.value)}
                    />
                  )}
                </div>
              </div>

              {/* Skills Tracker */}
              <div className="jo-section-card" id="skills">
                <div className="jo-section-header">
                  <div className="jo-section-icon"><BrainCircuit size={18} /></div>
                  <h3 className="jo-section-title">Skills Tracker</h3>
                </div>

                <div className="jo-skills-input-wrapper-v2">
                  <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '12px', padding: '6px', gap: '6px', alignItems: 'center' }}>
                    <motion.button
                      layout
                      type="button"
                      onClick={() => setSkillType("Pref")}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: skillType === "Pref" ? '8px 16px' : '8px',
                        borderRadius: '10px', border: 'none',
                        background: skillType === "Pref" ? '#ffffff' : 'transparent',
                        boxShadow: skillType === "Pref" ? '0 2px 5px rgba(0,0,0,0.06)' : 'none',
                        cursor: 'pointer', transition: 'all 0.2s',
                        overflow: 'hidden', whiteSpace: 'nowrap'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: skillType === "Pref" ? 'var(--primary)' : '#94a3b8' }}>
                        <ThumbsUp size={16} strokeWidth={skillType === "Pref" ? 2.5 : 2} />
                      </div>
                      {skillType === "Pref" && (
                        <motion.span 
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--primary)' }}
                        >
                          Preferred
                        </motion.span>
                      )}
                    </motion.button>
                    <motion.button
                      layout
                      type="button"
                      onClick={() => setSkillType("Req")}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: skillType === "Req" ? '8px 16px' : '8px',
                        borderRadius: '10px', border: 'none',
                        background: skillType === "Req" ? '#ffffff' : 'transparent',
                        boxShadow: skillType === "Req" ? '0 2px 5px rgba(0,0,0,0.06)' : 'none',
                        cursor: 'pointer', transition: 'all 0.2s',
                        overflow: 'hidden', whiteSpace: 'nowrap'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: skillType === "Req" ? '#ea580c' : '#94a3b8' }}>
                        <Asterisk size={16} strokeWidth={skillType === "Req" ? 2.5 : 2} />
                      </div>
                      {skillType === "Req" && (
                        <motion.span 
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          style={{ fontSize: '0.8rem', fontWeight: 600, color: '#ea580c' }}
                        >
                          Required
                        </motion.span>
                      )}
                    </motion.button>
                  </div>
                  <input
                    type="text"
                    placeholder="Add a skill (e.g. React)"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleAddSkill}
                  />
                  <button type="button" className="jo-skills-add-btn-v2" onClick={handleAddSkill}>
                    <Plus size={18} />
                  </button>
                </div>

                <div className="jo-skills-container-v2">
                  <div className="jo-skills-list">
                    {skillsReq.map(skill => (
                      <div className="jo-skill-tag req" key={`req-${skill}`}>
                        {skill} <span className="jo-skill-badge req-badge" onClick={() => {
                          setSkillsReq(skillsReq.filter(s => s !== skill));
                          setSkillsPref([...skillsPref, skill]);
                        }}>Req</span>
                        <div className="jo-tag-remove" onClick={() => handleRemoveSkillReq(skill)}><X size={12} /></div>
                      </div>
                    ))}
                    {skillsPref.map(skill => (
                      <div className="jo-skill-tag" key={`pref-${skill}`}>
                        {skill} <span className="jo-skill-badge pref-badge" onClick={() => {
                          setSkillsPref(skillsPref.filter(s => s !== skill));
                          setSkillsReq([...skillsReq, skill]);
                        }}>Pref</span>
                        <div className="jo-tag-remove" onClick={() => handleRemoveSkillPref(skill)}><X size={12} /></div>
                      </div>
                    ))}
                    {skillsReq.length === 0 && skillsPref.length === 0 && (
                      <div style={{ color: "#94a3b8", fontSize: "0.85rem", fontStyle: "italic" }}>No skills added yet.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Pipeline Stages */}
            <div className="jo-section-card" id="stages">
              <div className="jo-section-header">
                <div className="jo-section-icon"><List size={18} /></div>
                <h3 className="jo-section-title">Pipeline Stages</h3>
              </div>
              <p style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: "16px" }}>
                Define the application workflow stages for this opportunity. You can add new stages and reorder existing ones.
              </p>
              
              <div className="jo-form-group">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                  {stages.map((stage, idx) => {
                    const isFixed = ['Applied', 'Declined', 'Rejected'].includes(stage);
                    const canMoveLeft = idx > 1 && stage !== 'Applied' && stage !== 'Declined' && stage !== 'Rejected';
                    const canMoveRight = idx < stages.length - 3 && stage !== 'Applied' && stage !== 'Declined' && stage !== 'Rejected';
                    return (
                      <div key={idx} 
                        draggable={!isFixed}
                        onDragStart={(e) => {
                          setDraggedStageIdx(idx);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (draggedStageIdx !== null && draggedStageIdx !== idx) {
                            let targetIdx = idx;
                            if (targetIdx === 0) targetIdx = 1;
                            if (targetIdx >= stages.length - 2) targetIdx = stages.length - 3;
                            
                            const newStages = [...stages];
                            const item = newStages.splice(draggedStageIdx, 1)[0];
                            newStages.splice(targetIdx, 0, item);
                            setStages(newStages);
                          }
                          setDraggedStageIdx(null);
                        }}
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '6px', 
                          padding: '6px 12px', background: 'rgba(5, 150, 105, 0.1)', 
                          borderRadius: '20px', border: '1px solid rgba(5, 150, 105, 0.2)',
                          fontSize: '0.85rem', fontWeight: 600, color: '#059669',
                          cursor: !isFixed ? 'grab' : 'default',
                          opacity: draggedStageIdx === idx ? 0.5 : 1
                        }}>
                        {canMoveLeft && (
                          <button type="button" onClick={() => {
                            const newStages = [...stages];
                            [newStages[idx - 1], newStages[idx]] = [newStages[idx], newStages[idx - 1]];
                            setStages(newStages);
                          }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#059669', opacity: 0.7 }}>&larr;</button>
                        )}
                        {stage}
                        {canMoveRight && (
                          <button type="button" onClick={() => {
                            const newStages = [...stages];
                            [newStages[idx + 1], newStages[idx]] = [newStages[idx], newStages[idx + 1]];
                            setStages(newStages);
                          }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#059669', opacity: 0.7 }}>&rarr;</button>
                        )}
                        {!isFixed && (
                          <X size={14} style={{ cursor: 'pointer', opacity: 0.7, marginLeft: '4px' }} onClick={() => {
                            setStages(stages.filter(s => s !== stage));
                          }}/>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '8px', maxWidth: '400px' }}>
                  <input 
                    className="jo-input" 
                    placeholder="Add a custom stage..." 
                    value={newStage} 
                    onChange={e => setNewStage(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (newStage.trim() && !stages.includes(newStage.trim())) {
                          const insertPos = stages.length >= 2 ? stages.length - 2 : stages.length;
                          const newStages = [...stages];
                          newStages.splice(insertPos, 0, newStage.trim());
                          setStages(newStages);
                          setNewStage("");
                        }
                      }
                    }}
                  />
                  <button 
                    type="button" 
                    className="jo-btn jo-btn-secondary"
                    onClick={() => {
                      if (newStage.trim() && !stages.includes(newStage.trim())) {
                        const insertPos = stages.length >= 2 ? stages.length - 2 : stages.length;
                        const newStages = [...stages];
                        newStages.splice(insertPos, 0, newStage.trim());
                        setStages(newStages);
                        setNewStage("");
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="jo-section-card" id="description">
              <div className="jo-section-header">
                <div className="jo-section-icon"><FileText size={18} /></div>
                <h3 className="jo-section-title">Job Description</h3>
              </div>
              <div className="jo-form-group" style={{ marginBottom: 0 }}>
                <RichTextEditor
                  value={description}
                  onChange={(content) => setDescription(content)}
                  placeholder="Paste or type the job description here..."
                />
              </div>
            </div>

            <div style={{
              position: "sticky",
              bottom: 0,
              background: "#f8fafc", /* Match jo-content background */
              margin: "auto -16px 0 -16px",
              padding: "16px 16px 0px 16px",
              zIndex: 10
            }}>
              <div
                className="jo-form-actions"
                style={{
                  background: "#ffffff",
                  padding: "12px 20px",
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  margin: 0
                }}
              >
                <button type="button" className="jo-btn-secondary" onClick={() => navigate("/dashboard/job-opportunities")} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {isEditing ? "Update & Publish" : "Review & Publish Job"}
                </button>
              </div>
            </div>

          </div>
        </form>
      )}

      {/* ── Document Viewer Modal ── */}
      {docPreviewUrl && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '24px 80px' }}
          onClick={() => { setDocPreviewUrl(null); setDocLoading(false); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, borderRadius: '20px 20px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '8px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileText size={18} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>Document Preview</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ResumeZoomControls
                  zoom={docZoom}
                  onZoomChange={setDocZoom}
                  onSearchClick={() => docViewerRef.current?.openSearch()}
                  onDownloadClick={() => {
                    const a = document.createElement("a");
                    a.href = docPreviewUrl;
                    a.download = sourceDocName || "document";
                    a.click();
                  }}
                />
                <button
                  onClick={() => { setDocPreviewUrl(null); setDocLoading(false); }}
                  style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, padding: '8px 14px', borderRadius: '8px', transition: 'all 0.2s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'; }}
                >
                  <X size={16} /> Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              {docLoading && (
                <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', zIndex: 1 }}>
                  <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 500 }}>Loading Document...</span>
                </div>
              )}
              <ResumeViewer
                ref={docViewerRef}
                key={docPreviewUrl}
                url={docPreviewUrl}
                zoom={docZoom}
                onZoomChange={setDocZoom}
                onLoaded={() => setDocLoading(false)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default JobOpportunityFormPage;
